import type {
    Translatable,
    UseTranslateResult,
    ValueTranslator} from "@towni/common";
import {
    fromTranslatableString,
} from "@towni/common";
import type { ZodError } from "zod";

/**
 * Parses the error messages from a ZodError and returns them as an array of Translatable or string values.
 *
 * @param error - The ZodError object containing validation issues.
 * @param translator - (Optional) A translation function or object that can be used to translate the error messages.
 *                      If provided, the error messages will be translated using this translator.
 *                      It can be either a `UseTranslateResult` or a `ValueTranslator`.
 * @returns An array of Translatable or string values representing the parsed error messages.
 *
 * @example
 * ```typescript
 * import { ZodError } from "zod";
 * import { parseZodErrorMessages } from "./parse-zod-error-messages";
 *
 * const error = new ZodError([...]);
 * const messages = parseZodErrorMessages(error);
 * // messages will be an array of Translatable values
 *
 * const translator = (message: Translatable) => `Translated: ${message}`;
 * const translatedMessages = parseZodErrorMessages(error, translator);
 * // translatedMessages will be an array of translated string values
 * ```
 */
function parseZodErrorMessages(error: ZodError<unknown>): Translatable[];
function parseZodErrorMessages(
    error: ZodError<unknown>,
    translator: UseTranslateResult | ValueTranslator,
): string[];
function parseZodErrorMessages(
    error: ZodError<unknown>,
    translator?: UseTranslateResult | ValueTranslator,
): (Translatable | string)[] {
    const messages = error.issues.map(issue => {
        const message = fromTranslatableString(issue.message);
        if (!translator) return message;
        return `[${issue.path.join(".")}] ${translator(message)}`;
    });

    return messages;
}

export { parseZodErrorMessages };
